import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Checkbox } from '../atoms/Checkbox';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { Radio } from '../atoms/Radio/Radio';
import { Select } from '../atoms/Select/Select';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { LIGHT_GROUPS, ZONES, type LightGroup, type Zone } from '../../services/lightSurveyService';
import {
  FLOW_LEVELS,
  FLOW_METHODS,
  PUMP_FACINGS,
  PUMP_MODES,
  PUMP_OUTLETS,
  type EquipmentPumpOption,
  type FlowCoralInput,
  type FlowMethod,
  type FlowPointInput,
  type NewPumpInput,
  type PumpMode,
  type PumpOutlet,
  type VideoEstimate,
} from '../../services/flowSurveyService';
import { ParticleStopwatch, WatchTimer } from './FlowTimers';
import { FlowVideoEstimate } from './FlowVideoEstimate';
import '../lighting/Lighting.css';
import './FlowMap.css';

export type FlowEditorMode = 'point' | 'pump' | 'coral';

export interface CoralOption {
  readonly id: string;
  readonly name: string;
}

/** What is being edited — null: adding a new one. */
export type FlowEditTarget =
  | {
      kind: 'point';
      id: string;
      x: number;
      y: number;
      zone: Zone | null;
      method: FlowMethod;
      levelMin: number;
      levelMax: number;
      speedMinCms: number | null;
      speedMaxCms: number | null;
      alternating: boolean;
    }
  | {
      kind: 'pump';
      id: string;
      x: number;
      y: number;
      label: string;
      outlets: readonly PumpOutlet[];
      nominalFlowLph: number | null;
      mode: PumpMode;
      minPercent: number;
      maxPercent: number;
      cycleSeconds: number | null;
    }
  | { kind: 'coral'; id: string; x: number; y: number; zone: Zone | null; livestockId: string | null; label: string | null; careGroup: LightGroup | null };

export interface FlowPlacementEditorProps {
  mode: FlowEditorMode;
  onModeChange: (mode: FlowEditorMode) => void;
  /** Position picked on the photo (0–1); the fields can also be typed. */
  position: { x: number; y: number } | null;
  onPositionChange: (position: { x: number; y: number } | null) => void;
  editing: FlowEditTarget | null;
  onCancelEdit: () => void;
  corals: readonly CoralOption[];
  /** Pumps from the aquarium's equipment; picked instead of typing a name. */
  equipmentPumps?: readonly EquipmentPumpOption[];
  pending: boolean;
  failed: boolean;
  onSavePoint: (input: FlowPointInput, id: string | null) => void;
  onSavePump: (input: NewPumpInput, id: string | null) => void;
  onSaveCoral: (input: Required<Pick<FlowCoralInput, 'x' | 'y'>> & FlowCoralInput, id: string | null) => void;
  /** The AI's estimate from video frames (phase 5); without it the video method is not offered. */
  estimateVideo?: (frames: Blob[], durationSeconds: number, position: { x: number; y: number } | null) => Promise<VideoEstimate>;
}

const toPercent = (v: number | undefined): string => (v === undefined ? '' : String(Math.round(v * 1000) / 10));
const toOutlet = (value: string): PumpOutlet => ((PUMP_FACINGS as readonly string[]).includes(value) ? (value as PumpOutlet) : Number(value));
const str = (v: number | null | undefined): string => (v === null || v === undefined ? '' : String(v));

function parsePercent(value: string): number | null {
  if (value.trim() === '') return null;
  const n = Number(value.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n / 100 : null;
}

/** A number in [min, max] (integer when `integer`), or null. */
function parseNumber(value: string, min: number, max: number, integer = false): number | null {
  if (value.trim() === '') return null;
  const n = Number(value.replace(',', '.'));
  if (!Number.isFinite(n) || n < min || n > max || (integer && !Number.isInteger(n))) return null;
  return n;
}

/**
 * The form beside the photo: add or edit a flow reading (method, lowest and
 * highest level or cm/s, direction changes), a pump (model, nominal flow,
 * direction, programme) or a coral. The position is a pair of percentage
 * fields — tapping the photo fills them, keyboard and screen-reader users type them.
 */
export function FlowPlacementEditor({
  mode,
  onModeChange,
  position,
  onPositionChange,
  editing,
  onCancelEdit,
  corals,
  equipmentPumps = [],
  pending,
  failed,
  onSavePoint,
  onSavePump,
  onSaveCoral,
  estimateVideo,
}: FlowPlacementEditorProps) {
  const { t } = useTranslation();
  const [x, setX] = useState(toPercent(position?.x));
  const [y, setY] = useState(toPercent(position?.y));
  const [zone, setZone] = useState<Zone | ''>('');
  // Reading
  const [method, setMethod] = useState<FlowMethod>('visual');
  const [levelMin, setLevelMin] = useState('');
  const [levelMax, setLevelMax] = useState('');
  const [speedMin, setSpeedMin] = useState('');
  const [speedMax, setSpeedMax] = useState('');
  const [alternating, setAlternating] = useState(false);
  // Pump
  // Which equipment pump is chosen ('' = another pump, typed by name).
  const [pumpSource, setPumpSource] = useState(equipmentPumps[0]?.key ?? '');
  // Another pump: added to the aquarium's equipment as this type, brand and model.
  const [pumpType, setPumpType] = useState<EquipmentPumpOption['type']>('flow_pump');
  const [pumpBrand, setPumpBrand] = useState('');
  const [pumpModel, setPumpModel] = useState('');
  const [direction, setDirection] = useState('0');
  // Pumps with two outlets: the second one; '' = one outlet.
  const [secondOutlet, setSecondOutlet] = useState('');
  const [nominal, setNominal] = useState('');
  const [pumpMode, setPumpMode] = useState<PumpMode>('random');
  const [minPercent, setMinPercent] = useState('');
  const [maxPercent, setMaxPercent] = useState('');
  const [cycle, setCycle] = useState('');
  // Coral
  const [livestockId, setLivestockId] = useState('');
  const [label, setLabel] = useState('');
  const [group, setGroup] = useState<LightGroup | ''>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // A tap on the photo moves the position fields.
  useEffect(() => {
    setX(toPercent(position?.x));
    setY(toPercent(position?.y));
  }, [position?.x, position?.y]);

  // Editing loads the item into the form.
  useEffect(() => {
    setErrors({});
    if (!editing) return;
    if (editing.kind === 'point') {
      setZone(editing.zone ?? '');
      setMethod(editing.method);
      setLevelMin(String(editing.levelMin));
      setLevelMax(String(editing.levelMax));
      setSpeedMin(str(editing.speedMinCms));
      setSpeedMax(str(editing.speedMaxCms));
      setAlternating(editing.alternating);
    } else if (editing.kind === 'pump') {
      // An existing pump whose name is one of the equipment pumps shows as that choice.
      const match = equipmentPumps.find((p) => p.name === editing.label);
      setPumpSource(match?.key ?? '');
      // A pump named before it was in the equipment: its name becomes the model, to be completed.
      setPumpBrand('');
      setPumpModel(match ? '' : editing.label);
      setDirection(String(editing.outlets[0] ?? 0));
      setSecondOutlet(editing.outlets[1] !== undefined ? String(editing.outlets[1]) : '');
      setNominal(str(editing.nominalFlowLph));
      setPumpMode(editing.mode);
      setMinPercent(String(editing.minPercent));
      setMaxPercent(String(editing.maxPercent));
      setCycle(str(editing.cycleSeconds));
    } else {
      setZone(editing.zone ?? '');
      setLivestockId(editing.livestockId ?? '');
      setLabel(editing.label ?? '');
      setGroup(editing.careGroup ?? '');
    }
  }, [editing, equipmentPumps]);

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const next: Record<string, string> = {};
    const px = parsePercent(x);
    const py = parsePercent(y);
    if (px === null) next.x = String(t('flow.editor.positionInvalid'));
    if (py === null) next.y = String(t('flow.editor.positionInvalid'));

    if (mode === 'point') {
      let reading: FlowPointInput | null = null;
      if (method === 'visual' || method === 'video') {
        const lo = parseNumber(levelMin, 0, 4, true);
        const hi = parseNumber(levelMax, 0, 4, true);
        if (lo === null) next.levelMin = String(t('flow.editor.levelRequired'));
        if (hi === null) next.levelMax = String(t('flow.editor.levelRequired'));
        if (lo !== null && hi !== null && lo > hi) next.levelMin = String(t('flow.editor.rangeOrder'));
        if (lo !== null && hi !== null && lo <= hi && px !== null && py !== null) reading = { x: px, y: py, method, level_min: lo, level_max: hi };
      } else {
        const lo = parseNumber(speedMin, 0, 300);
        const hi = parseNumber(speedMax, 0, 300);
        if (lo === null) next.speedMin = String(t('flow.editor.speedInvalid'));
        if (hi === null) next.speedMax = String(t('flow.editor.speedInvalid'));
        if (lo !== null && hi !== null && lo > hi) next.speedMin = String(t('flow.editor.rangeOrder'));
        if (lo !== null && hi !== null && lo <= hi && px !== null && py !== null) reading = { x: px, y: py, method, speed_min: lo, speed_max: hi };
      }
      setErrors(next);
      if (Object.keys(next).length > 0 || !reading) return;
      onSavePoint({ ...reading, zone: zone || null, alternating }, editing?.kind === 'point' ? editing.id : null);
    } else if (mode === 'pump') {
      const lo = parseNumber(minPercent, 0, 100, true);
      const hi = parseNumber(maxPercent, 1, 100, true);
      const flow = nominal.trim() === '' ? undefined : parseNumber(nominal, 1, 100000, true);
      const seconds = cycle.trim() === '' ? undefined : parseNumber(cycle, 1, 86400, true);
      const chosen = equipmentPumps.find((p) => p.key === pumpSource);
      if (!chosen && pumpBrand.trim() === '' && pumpModel.trim() === '') next.pumpModel = String(t('flow.editor.pumpNameRequired'));
      if (lo === null) next.minPercent = String(t('flow.editor.percentInvalid'));
      if (hi === null) next.maxPercent = String(t('flow.editor.percentInvalid'));
      if (lo !== null && hi !== null && lo > hi) next.minPercent = String(t('flow.editor.rangeOrder'));
      if (flow === null) next.nominal = String(t('flow.editor.nominalInvalid'));
      if (seconds === null) next.cycle = String(t('flow.editor.cycleInvalid'));
      if (secondOutlet === direction) next.secondOutlet = String(t('flow.editor.outletsDistinct'));
      setErrors(next);
      if (Object.keys(next).length > 0) return;
      onSavePump(
        {
          x: px!,
          y: py!,
          ...(chosen ? { label: chosen.name } : { equipment: { type: pumpType, brand: pumpBrand.trim(), model: pumpModel.trim() } }),
          outlets: [toOutlet(direction), ...(secondOutlet ? [toOutlet(secondOutlet)] : [])],
          nominal_flow_lph: flow ?? null,
          mode: pumpMode,
          min_percent: lo!,
          max_percent: hi!,
          cycle_seconds: seconds ?? null,
        },
        editing?.kind === 'pump' ? editing.id : null,
      );
    } else {
      if (!livestockId && label.trim() === '') next.coral = String(t('flow.editor.coralRequired'));
      setErrors(next);
      if (Object.keys(next).length > 0) return;
      onSaveCoral(
        { x: px!, y: py!, zone: zone || null, livestock_id: livestockId || null, label: livestockId ? null : label.trim(), care_group: group || null },
        editing?.kind === 'coral' ? editing.id : null,
      );
    }
  };

  const levelOptions = (placeholder: string) => [
    { value: '', label: placeholder },
    ...FLOW_LEVELS.map((l) => ({ value: String(l), label: String(t('flow.levels.option', { level: l, name: t(`flow.levels.${l}.name`), sign: t(`flow.levels.${l}.sign`) })) })),
  ];

  const isEdit = editing !== null;
  const addLabel = { point: 'flow.editor.addPoint', pump: 'flow.editor.addPump', coral: 'flow.editor.addCoral' }[mode];
  return (
    <form onSubmit={submit} noValidate data-testid="flow-placement-editor">
      <Stack gap="4">
        {!isEdit && (
          <fieldset className="lighting__fieldset">
            <legend className="lighting__legend-title">{t('flow.editor.modeLegend')}</legend>
            <Radio name="flow-editor-mode" label={t('flow.editor.modePoint')} checked={mode === 'point'} onChange={() => onModeChange('point')} />
            <Radio name="flow-editor-mode" label={t('flow.editor.modePump')} checked={mode === 'pump'} onChange={() => onModeChange('pump')} />
            <Radio name="flow-editor-mode" label={t('flow.editor.modeCoral')} checked={mode === 'coral'} onChange={() => onModeChange('coral')} />
          </fieldset>
        )}
        <Text color="secondary" size="sm">{t(`flow.editor.hint.${mode}`)}</Text>
        <Inline gap="3" align="start">
          <Input id="flow-pos-x" type="number" inputMode="decimal" min={0} max={100} step={0.1} label={t('flow.editor.x')} value={x} onChange={(e) => setX(e.target.value)} error={errors.x} />
          <Input id="flow-pos-y" type="number" inputMode="decimal" min={0} max={100} step={0.1} label={t('flow.editor.y')} value={y} onChange={(e) => setY(e.target.value)} error={errors.y} />
        </Inline>

        {mode === 'point' && (
          <Stack gap="4">
            <fieldset className="lighting__fieldset">
              <legend className="lighting__legend-title">{t('flow.editor.method')}</legend>
              {FLOW_METHODS.filter((m) => m !== 'video' || estimateVideo || method === 'video').map((m) => (
                <Radio key={m} name="flow-method" label={t(`flow.methods.${m}`)} checked={method === m} onChange={() => setMethod(m)} />
              ))}
            </fieldset>
            {method === 'visual' || method === 'video' ? (
              <Stack gap="3">
                {method === 'visual' ? (
                  <>
                    <Text size="sm">{t('flow.editor.watchHint')}</Text>
                    <WatchTimer />
                  </>
                ) : (
                  estimateVideo && (
                    <FlowVideoEstimate
                      estimate={(frames, seconds) => {
                        const px = parsePercent(x);
                        const py = parsePercent(y);
                        return estimateVideo(frames, seconds, px !== null && py !== null ? { x: px, y: py } : null);
                      }}
                      onResult={(est) => {
                        setLevelMin(String(est.levelMin));
                        setLevelMax(String(est.levelMax));
                        setAlternating(Boolean(est.alternating));
                      }}
                    />
                  )
                )}
                <Select id="flow-level-min" label={t('flow.editor.levelMin')} value={levelMin} options={levelOptions(String(t('flow.editor.chooseLevel')))} onChange={(e) => setLevelMin(e.target.value)} error={errors.levelMin} />
                <Select id="flow-level-max" label={t('flow.editor.levelMax')} value={levelMax} options={levelOptions(String(t('flow.editor.chooseLevel')))} onChange={(e) => setLevelMax(e.target.value)} error={errors.levelMax} />
              </Stack>
            ) : (
              <Stack gap="3">
                {method === 'particle' && (
                  <ParticleStopwatch onUse={(end, speed) => (end === 'min' ? setSpeedMin(String(speed)) : setSpeedMax(String(speed)))} />
                )}
                {method === 'meter' && <Text size="sm">{t('flow.editor.meterHint')}</Text>}
                <Inline gap="3" align="start">
                  <Input id="flow-speed-min" type="number" inputMode="decimal" min={0} max={300} step={0.1} label={t('flow.editor.speedMin')} value={speedMin} onChange={(e) => setSpeedMin(e.target.value)} error={errors.speedMin} />
                  <Input id="flow-speed-max" type="number" inputMode="decimal" min={0} max={300} step={0.1} label={t('flow.editor.speedMax')} value={speedMax} onChange={(e) => setSpeedMax(e.target.value)} error={errors.speedMax} />
                </Inline>
              </Stack>
            )}
            <Checkbox label={t('flow.editor.alternating')} checked={alternating} onChange={(e) => setAlternating(e.target.checked)} />
          </Stack>
        )}

        {mode === 'pump' && (
          <Stack gap="3">
            {equipmentPumps.length > 0 ? (
              <Select
                id="flow-pump-equipment"
                label={t('flow.editor.pumpFromEquipment')}
                value={pumpSource}
                options={[
                  ...equipmentPumps.map((p) => ({ value: p.key, label: `${p.name} (${String(t(`aquarium.form.equipmentTypes.${p.type}`))})` })),
                  { value: '', label: String(t('flow.editor.pumpOther')) },
                ]}
                onChange={(e) => setPumpSource(e.target.value)}
              />
            ) : (
              <Text size="sm" color="secondary">{t('flow.editor.noEquipmentPumps')}</Text>
            )}
            {pumpSource === '' && (
              <Stack gap="3" data-testid="flow-pump-new">
                <Select
                  id="flow-pump-type"
                  label={t('flow.editor.pumpType')}
                  value={pumpType}
                  options={(['flow_pump', 'return_pump'] as const).map((type) => ({ value: type, label: String(t(`aquarium.form.equipmentTypes.${type}`)) }))}
                  onChange={(e) => setPumpType(e.target.value as EquipmentPumpOption['type'])}
                />
                <Inline gap="3" align="start">
                  <Input id="flow-pump-brand" label={t('flow.editor.pumpBrand')} placeholder={t('flow.editor.pumpBrandPlaceholder')} value={pumpBrand} onChange={(e) => setPumpBrand(e.target.value)} maxLength={100} />
                  <Input id="flow-pump-model" label={t('flow.editor.pumpModel')} placeholder={t('flow.editor.pumpModelPlaceholder')} value={pumpModel} onChange={(e) => setPumpModel(e.target.value)} error={errors.pumpModel} maxLength={100} />
                </Inline>
                <Text size="sm" color="secondary">{t('flow.editor.addsToEquipment')}</Text>
              </Stack>
            )}
            <Input id="flow-pump-nominal" type="number" inputMode="numeric" min={1} max={100000} label={t('flow.editor.nominal')} helperText={t('flow.editor.nominalHint')} value={nominal} onChange={(e) => setNominal(e.target.value)} error={errors.nominal} />
            <Select
              id="flow-pump-direction"
              label={t('flow.editor.direction')}
              value={direction}
              options={PUMP_OUTLETS.map((d) => ({ value: String(d), label: String(t(`flow.directions.${d}`)) }))}
              onChange={(e) => setDirection(e.target.value)}
            />
            <Select
              id="flow-pump-second-outlet"
              label={t('flow.editor.secondOutlet')}
              hint={t('flow.editor.secondOutletHint')}
              value={secondOutlet}
              options={[{ value: '', label: String(t('flow.editor.secondOutletNone')) }, ...PUMP_OUTLETS.map((d) => ({ value: String(d), label: String(t(`flow.directions.${d}`)) }))]}
              onChange={(e) => setSecondOutlet(e.target.value)}
              error={errors.secondOutlet}
            />
            <Select
              id="flow-pump-mode"
              label={t('flow.editor.pumpMode')}
              value={pumpMode}
              options={PUMP_MODES.map((m) => ({ value: m, label: String(t(`flow.modes.${m}`)) }))}
              onChange={(e) => setPumpMode(e.target.value as PumpMode)}
            />
            <Inline gap="3" align="start">
              <Input id="flow-pump-min" type="number" inputMode="numeric" min={0} max={100} label={t('flow.editor.minPercent')} value={minPercent} onChange={(e) => setMinPercent(e.target.value)} error={errors.minPercent} />
              <Input id="flow-pump-max" type="number" inputMode="numeric" min={1} max={100} label={t('flow.editor.maxPercent')} value={maxPercent} onChange={(e) => setMaxPercent(e.target.value)} error={errors.maxPercent} />
            </Inline>
            <Input id="flow-pump-cycle" type="number" inputMode="numeric" min={1} max={86400} label={t('flow.editor.cycle')} helperText={t('flow.editor.cycleHint')} value={cycle} onChange={(e) => setCycle(e.target.value)} error={errors.cycle} />
          </Stack>
        )}

        {mode === 'coral' && (
          <Stack gap="3">
            <Select
              id="flow-coral-livestock"
              label={t('flow.editor.coralFromLivestock')}
              value={livestockId}
              options={[{ value: '', label: String(t('flow.editor.coralByName')) }, ...corals.map((c) => ({ value: c.id, label: c.name }))]}
              onChange={(e) => setLivestockId(e.target.value)}
            />
            {!livestockId && (
              <Input id="flow-coral-name" label={t('flow.editor.coralName')} value={label} onChange={(e) => setLabel(e.target.value)} error={errors.coral} maxLength={120} placeholder={t('flow.editor.coralNamePlaceholder')} />
            )}
            <Select
              id="flow-coral-group"
              label={t('flow.editor.group')}
              value={group}
              options={[{ value: '', label: String(t('lighting.groups.auto')) }, ...LIGHT_GROUPS.map((g) => ({ value: g, label: String(t(`lighting.groups.${g}`)) }))]}
              onChange={(e) => setGroup(e.target.value as LightGroup | '')}
            />
          </Stack>
        )}

        {mode !== 'pump' && (
          <Select
            id="flow-zone"
            label={t('flow.editor.zone')}
            value={zone}
            options={[{ value: '', label: String(t('lighting.zones.unknown')) }, ...ZONES.map((z) => ({ value: z, label: String(t(`lighting.zones.${z}`)) }))]}
            onChange={(e) => setZone(e.target.value as Zone | '')}
          />
        )}
        {failed && <Alert compact>{t('flow.editor.error')}</Alert>}
        <Inline gap="2" wrap>
          <Button type="submit" variant="secondary" isLoading={pending}>
            {t(isEdit ? 'flow.editor.save' : addLabel)}
          </Button>
          {isEdit && (
            <Button type="button" variant="ghost" onClick={() => { setErrors({}); onPositionChange(null); onCancelEdit(); }}>
              {t('flow.cancel')}
            </Button>
          )}
        </Inline>
      </Stack>
    </form>
  );
}

export default FlowPlacementEditor;
